import {
  User,
  Organization,
  authControllerIsAuthenticated,
  authControllerLogin,
  authControllerLogout,
  organizationControllerFindAll,
} from "@/shared/client";
import { HttpStatusCode } from "axios";
import { useEffect, useState, useCallback, useMemo } from "react";
import { Outlet, useNavigate } from "react-router";
import AuthContext from "./auth/auth.context";
import {
  getUserOrganizationsFromUser,
  hasCorrectRole,
  isUserOrganizationStaff,
  getAuthRedirectPath,
  getAdminsFromOrganization,
} from "./auth/auth.utils";
import {
  getLastSelectedOrganizationId,
  setLastSelectedOrganizationId,
} from "./auth/auth.storage";
import { SeoHead } from "@/shared/seo/seo-head";
import { AppAccessRole } from "./auth/auth.types";

const AuthProvider = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User>();
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization>();
  const [isLoading, setIsLoading] = useState(true);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const navigate = useNavigate();

  // Helper to restore or set default organization
  const restoreOrSetDefaultOrganization = useCallback(
    (userData: User, organizations: Organization[]): void => {
      if (organizations.length === 0) {
        setSelectedOrganization(undefined);
        return;
      }

      // Try to restore last selected organization from localStorage
      const lastSelectedOrgId = getLastSelectedOrganizationId(userData.id);
      if (lastSelectedOrgId) {
        const lastSelectedOrg = organizations.find(
          (org) => org.id === lastSelectedOrgId
        );
        if (lastSelectedOrg) {
          setSelectedOrganization(lastSelectedOrg);
          return;
        }
      }

      // Fallback to first organization
      setSelectedOrganization(organizations[0]);
    },
    []
  );

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await authControllerIsAuthenticated();
        if (response.data && response.data.user) {
          setIsAuthenticated(response.status === HttpStatusCode.Ok);
          setUser(response.data.user);
          // Restore last selected organization or set default
          const organizations = getUserOrganizationsFromUser(
            response.data.user
          );
          restoreOrSetDefaultOrganization(response.data.user, organizations);
        }
      } catch {
        setIsAuthenticated(false);
        setUser(undefined);
        setSelectedOrganization(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [restoreOrSetDefaultOrganization]);

  const login = useCallback(
    async (email: string, password: string): Promise<User | undefined> => {
      try {
        setIsLoading(true);
        const response = await authControllerLogin({
          body: { email, password },
        });

        if (response.status === HttpStatusCode.Created && response.data?.user) {
          setIsAuthenticated(true);
          setUser(response.data.user);
          // Restore last selected organization or set default
          const organizations = getUserOrganizationsFromUser(
            response.data.user
          );
          restoreOrSetDefaultOrganization(response.data.user, organizations);

          return response.data.user;
        }

        throw new Error("Login failed");
      } catch (error) {
        console.error("Login failed:", error);
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [restoreOrSetDefaultOrganization]
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await authControllerLogout();

      if (response.status === HttpStatusCode.Accepted) {
        setIsAuthenticated(false);
        setUser(undefined);
        setSelectedOrganization(undefined);
        navigate("/login", { replace: true });
        return; // Success - exit early
      }

      throw new Error("Logout failed");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const refreshAuthStatus = useCallback(async (): Promise<User | undefined> => {
    try {
      setIsLoading(true);
      const response = await authControllerIsAuthenticated();

      if (response.data?.user) {
        setIsAuthenticated(response.status === HttpStatusCode.Ok);
        setUser(response.data.user);
        // Restore last selected organization or set default
        const organizations = getUserOrganizationsFromUser(response.data.user);
        restoreOrSetDefaultOrganization(response.data.user, organizations);
        return response.data.user;
      }

      // No user data - reset state
      setIsAuthenticated(false);
      setUser(undefined);
      setSelectedOrganization(undefined);
      return undefined;
    } catch (error) {
      console.error("Failed to refresh auth status:", error);
      setIsAuthenticated(false);
      setUser(undefined);
      setSelectedOrganization(undefined);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [restoreOrSetDefaultOrganization]);

  // ==================== Authorization Computed Values ====================
  // Centralized in auth provider as single source of truth

  const hasSystemRole = useMemo(() => {
    if (!user || !user.roles) return false;
    return user.roles.some((role) => role.scope === "system");
  }, [user]);

  // Fetch all organizations if user has system role
  useEffect(() => {
    const fetchAllOrganizations = async () => {
      if (!user || !hasSystemRole) {
        setAllOrganizations([]);
        return;
      }

      try {
        const response = await organizationControllerFindAll();
        if (response.status === HttpStatusCode.Ok && response.data) {
          setAllOrganizations(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch all organizations:", error);
        setAllOrganizations([]);
      }
    };

    fetchAllOrganizations();
  }, [user, hasSystemRole]);

  // Organizations: returns all organizations for system users, user's organizations otherwise
  const organizations = useMemo(() => {
    if (!user) return [];

    if (hasSystemRole && allOrganizations.length > 0) {
      return allOrganizations;
    }

    return getUserOrganizationsFromUser(user);
  }, [user, hasSystemRole, allOrganizations]);

  // Get the user's role for the selected organization (only roles that can access app)
  const selectedOrganizationRole = useMemo((): AppAccessRole | undefined => {
    if (!user || !user.roles || !selectedOrganization) return undefined;

    const role = user.roles.find(
      (r) =>
        r.scope === "organization" &&
        r.organization?.id === selectedOrganization.id
    );

    const roleValue = role?.role;
    // Only return roles that can access the application (not ORGANIZATION_STAFF)
    if (
      roleValue === "ORGANIZATION_ADMIN" ||
      roleValue === "ORGANIZATION_OWNER" ||
      roleValue === "SYSTEM_ADMIN" ||
      roleValue === "SYSTEM_SUPPORT"
    ) {
      return roleValue as AppAccessRole;
    }
    return undefined;
  }, [user, selectedOrganization]);

  // Check if user has staff role in ANY organization
  const isStaffUser = useMemo(() => {
    if (!user || !user.roles) return false;
    return user.roles.some(
      (role) =>
        role.scope === "organization" && role.role === "ORGANIZATION_STAFF"
    );
  }, [user]);

  const canAccessApplication = useMemo(() => {
    return hasCorrectRole(user);
  }, [user]);

  const selectedOrganizationAdmins = useMemo(() => {
    return getAdminsFromOrganization(selectedOrganization);
  }, [selectedOrganization]);

  // Wrapper for setSelectedOrganization that also saves to localStorage
  const setSelectedOrganizationWithStorage = useCallback(
    (organization: Organization): void => {
      setSelectedOrganization(organization);
      if (user) {
        setLastSelectedOrganizationId(user.id, organization.id);
      }
    },
    [user]
  );

  // Memoize context value to prevent unnecessary rerenders
  const contextValue = useMemo(
    () => ({
      user,
      selectedOrganization,
      isAuthenticated,
      isLoading,
      login,
      logout,
      setSelectedOrganization: setSelectedOrganizationWithStorage,
      refreshAuthStatus,
      // Authorization computed values
      organizations,
      hasSystemRole,
      canAccessApplication,
      selectedOrganizationRole,
      isStaffUser,
      selectedOrganizationAdmins,
      // Helper functions for computing auth from User objects
      hasCorrectRole,
      isUserOrganizationStaff,
      getAuthRedirectPath,
    }),
    [
      user,
      selectedOrganization,
      isAuthenticated,
      isLoading,
      login,
      logout,
      refreshAuthStatus,
      organizations,
      hasSystemRole,
      canAccessApplication,
      selectedOrganizationRole,
      isStaffUser,
      selectedOrganizationAdmins,
      setSelectedOrganizationWithStorage,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      <SeoHead />
      <Outlet />
    </AuthContext.Provider>
  );
};

export default AuthProvider;
